/**
 * Place on map "By stations" (spec §17 rule 5), ArcGIS-free so karma can run it: reading a typed station into a ground
 * point on the chosen alignment, and the `place` body the API's `stationPoints` expects. The dialog only wires these up.
 */
import { FT, pointAt, ProjectAlignment, stationAt, stationName, stationRange } from '../../../../../../../shared/helpers/alignment-stationing';
import { parseStationInput } from '../../../../../../../shared/helpers/design-context';
import { DrawingPlaceRequest } from '../../../../../../../shared/models/drawing-sheet';

/** A sheet point at a station of the alignment; `map` is its preview, null off the alignment */
export interface StationPair {
  sheet: { x: number; y: number };
  /** Feet, to 0.01 ft: what is sent and what is previewed */
  station: number;
  offset_ft: number;
  side: 'L' | 'R' | null;
  check: boolean;
  map: { lat: number; lon: number } | null;
}

export type StationEntry =
  | { ok: true; station: number; offset_ft: number; side: 'L' | 'R' | null; map: { lat: number; lon: number } }
  | { ok: false; error: string };

/** "0+00 to 56+00" */
export function stationRangeText(alignment: ProjectAlignment): string {
  const { first, last } = stationRange(alignment.alignment);
  return `${stationName(first)} to ${stationName(last)}`;
}

/** The ground point of a station on the alignment, as the API's `stationPoints` places it (side R by default) */
export function stationGround(alignment: ProjectAlignment, station: number, offsetFt: number, side: 'L' | 'R' | null) {
  return pointAt(alignment.alignment, station, offsetFt, side ?? 'R');
}

/** The typed station, offset and side, checked as the API checks them, with the ground point they give */
export function readStationEntry(alignment: ProjectAlignment, input: { station: string; offset: string | number; side: '' | 'L' | 'R' }): StationEntry {
  const typed = parseStationInput(input.station);
  if (typed === null || typed === undefined || typed < 0 || typed > 1e8) {
    return { ok: false, error: 'Enter the station like 1018+50, or in feet.' };
  }
  // Rounded as sent, so the range check here is the API's
  const station = Math.round(typed * 100) / 100;
  const offset = String(input.offset ?? '').trim() === '' ? 0 : Number(input.offset);
  if (!Number.isFinite(offset) || offset < 0 || offset > 10000) {
    return { ok: false, error: 'Enter the offset from the line in feet, 0 to 10,000.' };
  }
  if (offset > 0 && !input.side) {
    return { ok: false, error: 'Choose the side of the line the point is on, looking up-station.' };
  }
  const side = offset > 0 ? (input.side as 'L' | 'R') : null;
  const map = stationGround(alignment, station, offset, side);
  if (!map) {
    return {
      ok: false,
      error: `STA ${stationName(station)} is not on ${alignment.name || 'the alignment'}. Its stations run ${stationRangeText(alignment)}.`,
    };
  }
  return { ok: true, station, offset_ft: offset, side, map };
}

/**
 * Why the stations can't be sent for this alignment, or null. The API finds it by name (trimmed, any case); with more
 * than one alignment an unnamed one is ambiguous (409) and a shared name finds the first, which may not be this one.
 */
export function alignmentNameProblem(alignments: ProjectAlignment[], chosen: ProjectAlignment | null): string | null {
  if (!chosen || alignments.length < 2) {
    return null;
  }
  const key = (name: string) => String(name ?? '').trim().toLowerCase();
  if (!key(chosen.name)) {
    return 'This alignment has no name, so the stations cannot be matched to it. Name it in the project\'s alignments first.';
  }
  if (alignments.filter(a => key(a.name) === key(chosen.name)).length > 1) {
    return `Another alignment is also named ${chosen.name.trim()}, so the stations cannot be matched to this one. Rename one of them first.`;
  }
  return null;
}

/** `POST …/place` by stations: offset and side only off the line, check only when true, the alignment by name */
export function stationPlaceBody(
  pairs: StationPair[],
  alignment: ProjectAlignment,
): Pick<DrawingPlaceRequest, 'control_points' | 'check_points' | 'station_points' | 'alignment'> {
  const name = String(alignment.name ?? '').trim();
  return {
    control_points: [],
    check_points: [],
    station_points: pairs.map(p => ({
      sheet: { x: p.sheet.x, y: p.sheet.y },
      station: p.station,
      ...(p.offset_ft > 0 ? { offset_ft: p.offset_ft, side: p.side } : {}),
      ...(p.check ? { check: true } : {}),
    })),
    ...(name ? { alignment: name } : {}),
  };
}

// Picking a station on the map (Sri) ---------------------------------------------------------------------------------

/** A map click this close to the line (screen pixels) is On the line, offset 0; Alt keeps the exact offset */
export const ON_LINE_PX = 8;

export interface StationSnap {
  /** Feet, to 0.01 ft (as sent) */
  station: number;
  /** Feet, to 0.01 ft; 0 on the line */
  offset_ft: number;
  side: 'L' | 'R' | null;
  onLine: boolean;
}

/**
 * A map point on the alignment: its nearest point's station (interpolated between the alignment's points), and the
 * perpendicular offset and side. `metresPerPx` is the map's ground resolution there; within ON_LINE_PX of the line it is
 * On the line unless `exact`. Null beyond either end: that is not a station of the alignment.
 */
export function snapToAlignment(alignment: ProjectAlignment, point: { lat: number; lon: number }, metresPerPx: number, exact = false): StationSnap | null {
  const at = stationAt(alignment.alignment, point);
  if (at.beyond) {
    return null;
  }
  const station = Math.round(at.station * 100) / 100;
  const px = (at.offset_ft * FT) / (metresPerPx || 1);
  if (!at.side || (!exact && px <= ON_LINE_PX)) {
    return { station, offset_ft: 0, side: null, onLine: true };
  }
  return { station, offset_ft: Math.round(at.offset_ft * 100) / 100, side: at.side, onLine: false };
}

/** The station field's text, to 0.01 ft: "1006+42.37", "1006+00" */
export function stationInputText(feet: number): string {
  const cents = Math.round(feet * 100);
  const hundreds = Math.floor(cents / 10000);
  const rest = (cents - hundreds * 10000) / 100;
  const whole = Math.floor(rest);
  const frac = Math.round((rest - whole) * 100);
  const decimals = frac ? `.${String(frac).padStart(2, '0').replace(/0$/, '')}` : '';
  return `${hundreds}+${String(whole).padStart(2, '0')}${decimals}`;
}

/** The map's hover readout: "STA 1006+42 · 12 ft R", "STA 1006+42 · on the line" */
export function stationReadout(snap: StationSnap): string {
  const sta = `STA ${stationName(Math.round(snap.station))}`;
  return snap.onLine ? `${sta} · on the line` : `${sta} · ${Math.round(snap.offset_ft)} ft ${snap.side}`;
}

/** A tick across the alignment at each multiple of `everyFt`: its station, the point on the line, and its two ends */
export function stationTicks(alignment: ProjectAlignment, everyFt: number, halfLengthFt: number) {
  const { first, last } = stationRange(alignment.alignment);
  const ticks: { station: number; at: { lat: number; lon: number }; left: { lat: number; lon: number }; right: { lat: number; lon: number } }[] = [];
  for (let station = Math.ceil(first / everyFt) * everyFt; station <= last + 1e-6; station += everyFt) {
    const at = pointAt(alignment.alignment, station);
    const left = pointAt(alignment.alignment, station, halfLengthFt, 'L');
    const right = pointAt(alignment.alignment, station, halfLengthFt, 'R');
    if (at && left && right) {
      ticks.push({ station, at, left, right });
    }
  }
  return ticks;
}

/** Station steps (feet) for labels and ticks: 1+00, 5+00, 10+00, 50+00, 100+00 */
export const STATION_STEPS = [100, 500, 1000, 5000, 10000];
/** Labels at least this far apart on screen; ticks at least TICK_GAP_PX */
export const LABEL_GAP_PX = 60;
export const TICK_GAP_PX = 8;

/**
 * Zoom-aware station marks (Sri): at `metresPerPx` (ground metres per screen pixel), the finest step whose labels sit
 * LABEL_GAP_PX apart, and the finest (never coarser) whose ticks sit TICK_GAP_PX apart. Null when even 100+00 is too
 * tight: no interval labels / no ticks (the ends are still labelled).
 */
export function stationIntervals(metresPerPx: number): { labelEvery: number | null; tickEvery: number | null } {
  const px = (step: number) => (step * FT) / (metresPerPx || 1);
  const labelEvery = STATION_STEPS.find(step => px(step) >= LABEL_GAP_PX) ?? null;
  const tickEvery = STATION_STEPS.find(step => px(step) >= TICK_GAP_PX && step <= (labelEvery ?? Infinity)) ?? null;
  return { labelEvery, tickEvery };
}

/**
 * The stations to label: always the alignment's first and last, and every `labelEvery` between them except those
 * closer than LABEL_GAP_PX to an end label (they would overlap it).
 */
export function stationLabelStations(first: number, last: number, labelEvery: number | null, metresPerPx: number): number[] {
  const gapFt = (LABEL_GAP_PX * (metresPerPx || 1)) / FT;
  const inner: number[] = [];
  if (labelEvery) {
    for (let station = Math.ceil(first / labelEvery) * labelEvery; station <= last; station += labelEvery) {
      if (station - first >= gapFt && last - station >= gapFt) {
        inner.push(station);
      }
    }
  }
  return [first, ...inner, last];
}

/** The lon / lat box of the alignment between two stations (clipped to it), for fitting the map; null when they miss it */
export function stationRangeBounds(alignment: ProjectAlignment, from: number, to: number): { xmin: number; ymin: number; xmax: number; ymax: number } | null {
  const range = stationRange(alignment.alignment);
  const a = Math.max(Math.min(from, to), range.first);
  const b = Math.min(Math.max(from, to), range.last);
  if (!(b > a)) {
    return null;
  }
  const steps = Math.max(1, Math.ceil((b - a) / 20));
  let box: { xmin: number; ymin: number; xmax: number; ymax: number } | null = null;
  for (let i = 0; i <= steps; i += 1) {
    const p = pointAt(alignment.alignment, a + ((b - a) * i) / steps);
    if (p) {
      box = box
        ? { xmin: Math.min(box.xmin, p.lon), ymin: Math.min(box.ymin, p.lat), xmax: Math.max(box.xmax, p.lon), ymax: Math.max(box.ymax, p.lat) }
        : { xmin: p.lon, ymin: p.lat, xmax: p.lon, ymax: p.lat };
    }
  }
  return box;
}

// Side and Offset (the API needs a side when offset_ft > 0) -------------------------------------------------------------

/** Left | On the line | Right, or none picked yet */
export type SideChoice = '' | 'L' | 'R' | 'on';

export interface SideOffset {
  side: SideChoice;
  offset: string;
}

const offsetFeet = (offset: string) => (String(offset ?? '').trim() === '' ? 0 : Number(offset));

/** On the line sets the offset to 0; Left or Right keep it */
export function chooseSide(state: SideOffset, side: SideChoice): SideOffset {
  return side === 'on' ? { side, offset: '0' } : { side, offset: state.offset };
}

/** An offset typed while On the line leaves no side picked: off the line, it needs Left or Right */
export function typeOffset(state: SideOffset, offset: string): SideOffset {
  return state.side === 'on' && offsetFeet(offset) > 0 ? { side: '', offset } : { side: state.side, offset };
}

/** The Side control's helper text, and whether it must be picked before Add station */
export function sideHelper(state: SideOffset): { text: string | null; needsSide: boolean } {
  const offset = offsetFeet(state.offset);
  if (state.side === '' && offset > 0) {
    return { text: 'Pick Left or Right', needsSide: true };
  }
  if (state.side === '') {
    return { text: 'Pick Left, On the line or Right', needsSide: true };
  }
  if ((state.side === 'L' || state.side === 'R') && !(offset > 0)) {
    return { text: 'On the line?', needsSide: false };
  }
  return { text: null, needsSide: false };
}

/** Add station: a readable station, an offset of 0 to 10,000 ft, and a side that goes with it */
export function stationEntryReady(stationText: string, state: SideOffset): boolean {
  const typed = parseStationInput(stationText);
  if (typed === null || typed === undefined || typed < 0 || typed > 1e8) {
    return false;
  }
  const offset = offsetFeet(state.offset);
  if (!Number.isFinite(offset) || offset < 0 || offset > 10000) {
    return false;
  }
  return !sideHelper(state).needsSide;
}

/** The side readStationEntry takes: none on the line */
export function entrySide(side: SideChoice): '' | 'L' | 'R' {
  return side === 'L' || side === 'R' ? side : '';
}
